import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Upload } from 'lucide-react';
import { useData } from '../../context/DataContext';

const AdminProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getProject, addProject, updateProject } = useData();

  const isEditing = !!id;
  const existingProject = isEditing ? getProject(id) : null;

  const [formData, setFormData] = useState({
    title: '',
    shortDescription: '',
    longDescription: '',
    status: 'Devam Eden',
    location: '',
    tags: [],
    videoUrl: '',
    coverImage: '',
    gallery: [],
  });

  const [newTag, setNewTag] = useState('');
  const [newGalleryUrl, setNewGalleryUrl] = useState('');

  useEffect(() => {
    if (existingProject) {
      setFormData(existingProject);
    }
  }, [existingProject]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const handleAddGalleryUrl = () => {
    if (newGalleryUrl.trim() && !formData.gallery.includes(newGalleryUrl.trim())) {
      setFormData((prev) => ({
        ...prev,
        gallery: [...prev.gallery, newGalleryUrl.trim()],
      }));
      setNewGalleryUrl('');
    }
  };

  const handleRemoveGalleryImage = (url) => {
    setFormData((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((g) => g !== url),
    }));
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'cover') {
          setFormData((prev) => ({ ...prev, coverImage: reader.result }));
        } else {
          setFormData((prev) => ({
            ...prev,
            gallery: [...prev.gallery, reader.result],
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.shortDescription || !formData.coverImage) {
      alert('Lütfen zorunlu alanları doldurun!');
      return;
    }

    if (isEditing) {
      updateProject(id, formData);
    } else {
      addProject(formData);
    }

    navigate('/admin/projeler');
  };

  return (
    <div>
      <button
        onClick={() => navigate('/admin/projeler')}
        className="flex items-center space-x-2 text-gray-400 hover:text-gold mb-6 transition-colors duration-300"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Geri Dön</span>
      </button>

      <h1 className="text-2xl font-bold text-white mb-8">
        {isEditing ? 'Proje Düzenle' : 'Yeni Proje Ekle'}
      </h1>

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-8">
        {/* Basic Info */}
        <div className="bg-anthracite-light rounded-lg border border-gold/10 p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Temel Bilgiler</h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Proje Adı *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300"
                placeholder="Örn: Kemalpaşa 150 Daire Projesi"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Durum *</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300"
                >
                  <option value="Devam Eden">Devam Eden</option>
                  <option value="Biten">Biten</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Konum *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300"
                  placeholder="Örn: Kemalpaşa, İzmir"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Kısa Açıklama *</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-3 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300 resize-none"
                placeholder="Proje kartlarında görünecek kısa açıklama (1-2 cümle)"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Detaylı Açıklama</label>
              <textarea
                name="longDescription"
                value={formData.longDescription}
                onChange={handleChange}
                rows={6}
                className="w-full px-4 py-3 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300 resize-none"
                placeholder="Proje detay sayfasında görünecek detaylı açıklama"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">YouTube Video URL (Opsiyonel)</label>
              <input
                type="url"
                name="videoUrl"
                value={formData.videoUrl || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300"
                placeholder="https://www.youtube.com/watch?v=XXXXX"
              />
              <p className="text-xs text-gray-500 mt-1">YouTube video linki ekleyin, proje detayında gösterilecek</p>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="bg-anthracite-light rounded-lg border border-gold/10 p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Etiketler</h2>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {formData.tags.map((tag, index) => (
              <span
                key={index}
                className="flex items-center space-x-2 px-3 py-1 bg-gold/10 text-gold rounded"
              >
                <span>{tag}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="hover:text-gold-light"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
              className="flex-1 px-4 py-2 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300"
              placeholder="Etiket ekle (Konut, Villa, Anahtar Teslim...)"
            />
            <button
              type="button"
              onClick={handleAddTag}
              className="px-4 py-2 bg-gold/10 text-gold rounded hover:bg-gold/20 transition-colors duration-300"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Cover Image */}
        <div className="bg-anthracite-light rounded-lg border border-gold/10 p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Kapak Görseli *</h2>
          
          {formData.coverImage && (
            <div className="mb-4">
              <img
                src={formData.coverImage}
                alt="Kapak"
                className="w-48 h-32 object-cover rounded"
              />
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">URL ile ekle</label>
              <input
                type="url"
                name="coverImage"
                value={formData.coverImage}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300"
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="text-center text-gray-400">veya</div>
            <div>
              <label className="flex items-center justify-center space-x-2 px-4 py-3 bg-anthracite border border-gold/20 border-dashed rounded cursor-pointer hover:border-gold transition-colors duration-300">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-gray-400">Dosya Yükle</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'cover')}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Gallery */}
        <div className="bg-anthracite-light rounded-lg border border-gold/10 p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Galeri Görselleri</h2>
          
          {formData.gallery.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mb-6">
              {formData.gallery.map((img, index) => (
                <div key={index} className="relative group">
                  <img
                    src={img}
                    alt={`Galeri ${index + 1}`}
                    className="w-full h-24 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveGalleryImage(img)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="url"
                value={newGalleryUrl}
                onChange={(e) => setNewGalleryUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGalleryUrl())}
                className="flex-1 px-4 py-2 bg-anthracite border border-gold/20 rounded text-white focus:border-gold focus:outline-none transition-colors duration-300"
                placeholder="Görsel URL'si ekle"
              />
              <button
                type="button"
                onClick={handleAddGalleryUrl}
                className="px-4 py-2 bg-gold/10 text-gold rounded hover:bg-gold/20 transition-colors duration-300"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="flex items-center justify-center space-x-2 px-4 py-3 bg-anthracite border border-gold/20 border-dashed rounded cursor-pointer hover:border-gold transition-colors duration-300">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-gray-400">Dosya Yükle</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'gallery')}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/projeler')}
            className="px-6 py-3 text-gray-400 hover:text-white transition-colors duration-300"
          >
            İptal
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-gold text-anthracite font-semibold rounded hover:bg-gold-light transition-colors duration-300"
          >
            {isEditing ? 'Değişiklikleri Kaydet' : 'Proje Ekle'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProjectForm;